<template>
  <div>
    <Header />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Employees Invites</h5>
        <p class="page-sub">View and manage all employees invites</p>
      </div>
      <div class="page-actions">

        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input type="text" class="search-input" placeholder="Search employees..."
            v-model="tableData.search" @input="debouncedFetch" />
        </div>

        <select v-model="tableData.status" @change="fetchEmployees()" class="filter-select">
          <option :value="['invited','completed','cancelled','accepted']">All Statuses</option>
          <option :value="['invited']">Invited</option>
          <option :value="['completed']">Completed</option>
          <option :value="['cancelled']">Cancelled</option>
          <option :value="['accepted']">Accepted</option>
        </select>

        <button class="btn-danger-outline"
          :disabled="permission.delete_employee != 1 || selectedModel.length === 0"
          @click="bulkDeleteOpen = true">
          <font-awesome-icon icon="trash" />
          Bulk Delete
          <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
        </button>

        <div class="dropdown-wrap" v-click-outside="closeDropdown">
          <button class="btn-primary-action" @click="dropdownOpen = !dropdownOpen">
            <font-awesome-icon icon="plus" />
            New Invite
            <font-awesome-icon icon="chevron-down" class="chevron" />
          </button>
          <div v-if="dropdownOpen" class="action-dropdown">
            <button class="action-dropdown-item" @click="handleNew('invite')">
              <div class="action-dropdown-icon"><font-awesome-icon icon="envelope" /></div>
              <div>
                <div class="action-dropdown-label">Invite Employee</div>
                <div class="action-dropdown-hint">Send invitation email</div>
              </div>
            </button>
            <button class="action-dropdown-item" @click="handleNew('bulk_invite')">
              <div class="action-dropdown-icon"><font-awesome-icon icon="envelope" /></div>
              <div>
                <div class="action-dropdown-label">Bulk Invite</div>
                <div class="action-dropdown-hint">Send invitations to multiple employees</div>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Data Card -->
    <div class="data-card">

      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading employees...</p>
      </div>

      <div v-else-if="permissionStatus === 403" class="state-block">
        <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
        <p class="state-title">Access Denied</p>
        <p class="state-sub">You don't have permission to view the employees directory.</p>
      </div>

      <div v-else-if="employees.length === 0" class="state-block">
        <div class="state-icon state-icon--blue"><font-awesome-icon icon="users" /></div>
        <p class="state-title">No employees found</p>
        <p class="state-sub">
          {{ tableData.search ? 'No results match your search.' : 'Employees will appear here once added.' }}
        </p>
      </div>

      <template v-else>
        <div class="table-toolbar">
          <span class="table-count">
            <strong>{{ employees.length }}</strong> employee{{ employees.length !== 1 ? 's' : '' }}
            <span v-if="selectedModel.length > 0" class="selected-hint">
              &middot; {{ selectedModel.length }} selected
            </span>
          </span>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-check">
                  <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Expires At</th>
                <th>Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="emp in employees" :key="emp.id"
                :class="{ 'row--selected row-clickable': selectedModel.includes(emp.id) }">

                <td class="col-check">
                  <input type="checkbox" class="row-check" v-model="selectedModel" :value="emp.id" />
                </td>

                <td>
                  <div class="emp-cell">
                    <div class="emp-avatar">{{ getInitials(emp.first_name, emp.last_name) }}</div>
                    <div>
                      <div class="emp-name">
                        {{ [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '—' }}
                      </div>
                    </div>
                  </div>
                </td>

                <td class="text-muted-cell">{{ emp.email }}</td>
                <td class="text-muted-cell">{{ emp.expires_at }}</td>

                <td>
                  <span class="status-badge" :class="inviteStatusClass(emp.status)">
                    {{ emp.status || '—' }}
                  </span>
                </td>

                <td class="col-actions" @click.stop>
                  <div class="action-btns">
                    <!-- Resend — show for any status except completed -->
                    <button
                      v-if="emp.status !== 'completed'"
                      class="action-icon-btn action-icon-btn--blue"
                      :class="{ 'action-icon-btn--loading': resendingId === emp.id }"
                      title="Resend invitation"
                      :disabled="resendingId === emp.id"
                      @click="openResendModal(emp)">
                      <span v-if="resendingId === emp.id"
                        class="spinner-border spinner-border-sm" role="status"></span>
                      <font-awesome-icon v-else icon="paper-plane" />
                    </button>

                    <!-- View details -->
                    <!-- <button class="action-icon-btn"
                      title="View details"
                      @click="$router.push('/employees/' + emp.id)">
                      <font-awesome-icon icon="chevron-right" />
                    </button> -->
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>


    <!-- INVITE EMPLOYEE MODAL -->
    <div class="modal-overlay" :class="{ open: inviteOpen }" @click.self="inviteOpen = false">
      <div class="modal-box">
        <div class="modal-hd">
          <div class="modal-hd-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
            <font-awesome-icon icon="envelope" />
          </div>
          <div>
            <span class="modal-ttl">Invite Employee</span>
            <p class="modal-sub">Send an invitation email</p>
          </div>
          <button class="modal-close-btn" @click="inviteOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <form @submit.prevent="submitInvite">
          <div class="modal-bd">
            <div class="field-group">
              <label class="field-label">First Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="inviteForm.first_name" required />
            </div>
            <div class="field-group">
              <label class="field-label">Last Name <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="inviteForm.last_name" required />
            </div>
            <div class="field-group">
              <label class="field-label">Official Email Address <span class="req">*</span></label>
              <input type="email" class="field-input" v-model="inviteForm.email" required />
            </div>
            <div class="field-group">
              <label class="field-label">Personal Email Address</label>
              <input type="email" class="field-input" v-model="inviteForm.personal_email" />
            </div>
          </div>
          <div class="modal-ft">
            <button type="button" class="btn-ghost" @click="inviteOpen = false">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="paper-plane" class="me-1" />
              {{ crudLoading ? 'Sending...' : 'Send Invitation' }}
            </button>
          </div>
        </form>
      </div>
    </div>


    <!-- BULK INVITE MODAL -->
    <div class="modal-overlay" :class="{ open: bulkInviteOpen }" @click.self="bulkInviteOpen = false">
      <div class="modal-box">
        <div class="modal-hd">
          <div class="modal-hd-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
            <font-awesome-icon icon="envelope" />
          </div>
          <div>
            <span class="modal-ttl">Bulk Invite</span>
            <p class="modal-sub">Send invitations to multiple employees via CSV</p>
          </div>
          <button class="modal-close-btn" @click="bulkInviteOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="modal-bd">
          <div class="upload-zone" :class="{ 'upload-zone--active': bulkFile }"
            @click="$refs.bulkFileInput.click()"
            @dragover.prevent
            @drop.prevent="onBulkFileDrop">
            <font-awesome-icon icon="file-csv" class="upload-icon" />
            <p class="upload-label">
              {{ bulkFile ? bulkFile.name : 'Click or drag a CSV file here' }}
            </p>
            <p class="upload-hint">Accepted: .csv, .xlsx</p>
            <input ref="bulkFileInput" type="file" accept=".csv,.xlsx" style="display:none"
              @change="bulkFile = $event.target.files[0]" />
          </div>

          <!-- Template download -->
          <div class="template-hint">
            <font-awesome-icon icon="info-circle" class="template-hint-icon" />
            <span>Not sure about the format?</span>
            <button type="button" class="btn-download-template" @click="downloadTemplate">
              <font-awesome-icon icon="download" class="me-1" />
              Download template
            </button>
          </div>

          <!-- Column reference -->
          <div class="template-cols">
            <p class="template-cols-title">Required & optional columns:</p>
            <div class="template-col-list">
              <span class="col-tag col-tag--required">first_name <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">last_name <span class="col-req">*</span></span>
              <span class="col-tag col-tag--required">official_email <span class="col-req">*</span></span>
              <span class="col-tag col-tag--optional">personal_email</span>
            </div>
            <p class="template-cols-note"><span class="col-req">*</span> required columns</p>
          </div>
        </div>
        <div class="modal-ft">
          <button type="button" class="btn-ghost" @click="bulkInviteOpen = false">Cancel</button>
          <button class="btn-primary-action" :disabled="!bulkFile || crudLoading" @click="submitBulkInvite">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="paper-plane" class="me-1" />
            {{ crudLoading ? 'Sending...' : 'Send Bulk Invites' }}
          </button>
        </div>
      </div>
    </div>


    <!-- BULK DELETE CONFIRM MODAL -->
    <div class="modal-overlay" :class="{ open: bulkDeleteOpen }" @click.self="bulkDeleteOpen = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-icon-wrap">
          <div class="modal-icon modal-icon--red">
            <font-awesome-icon icon="trash" />
          </div>
        </div>
        <div class="modal-body-clean">
          <p class="modal-heading">Delete {{ selectedModel.length }} Invite{{ selectedModel.length !== 1 ? 's' : '' }}?</p>
          <p class="modal-subtext">
            This will permanently delete the selected invite{{ selectedModel.length !== 1 ? 's' : '' }}.
            This action cannot be undone.
          </p>
        </div>
        <div class="modal-actions">
          <button class="modal-btn modal-btn--cancel" @click="bulkDeleteOpen = false">Cancel</button>
          <button class="modal-btn modal-btn--danger" :disabled="crudLoading" @click="confirmBulkDelete">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ crudLoading ? 'Deleting...' : 'Yes, Delete' }}
          </button>
        </div>
      </div>
    </div>

    <!-- RESEND CONFIRM MODAL -->
    <div class="modal-overlay" :class="{ open: resendOpen }" @click.self="resendOpen = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-icon-wrap">
          <div class="modal-icon modal-icon--blue">
            <font-awesome-icon icon="paper-plane" />
          </div>
        </div>
        <div class="modal-body-clean">
          <p class="modal-heading">Resend Invitation?</p>
          <p class="modal-subtext">
            A new invitation email will be sent to
            <strong>{{ resendTarget && resendTarget.email }}</strong>.
            Any previous invite link will be replaced.
          </p>
        </div>
        <div class="modal-actions">
          <button class="modal-btn modal-btn--cancel" @click="resendOpen = false">Cancel</button>
          <button class="modal-btn modal-btn--primary" :disabled="resendingId !== null" @click="confirmResend">
            <span v-if="resendingId !== null" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ resendingId !== null ? 'Sending...' : 'Yes, Resend' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="inviteOpen || bulkInviteOpen || uploadOpen || resendOpen || bulkDeleteOpen" class="modal-backdrop-custom" />

  </div>
</template>


<script>
import Header from './component/header.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

export default {
  name: 'EmployeesInvitesPage',

  components: { Header },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },

  data() {
    return {
      employees:        [],
      loading:          false,
      crudLoading:      false,
      permissionStatus: null,
      dropdownOpen:     false,
      selectedModel:    [],
      allSelected:      false,

      // Track which invite is currently being resent
      resendingId:  null,
      resendOpen:   false,
      resendTarget: null,
      bulkDeleteOpen: false,

      tableData: { page: 1,
        search: '',
        length: 10,
        status: ['invited','completed','cancelled','accepted'],
      },

      inviteOpen: false,
      inviteForm: { first_name: '', last_name: '', email: '', personal_email: '' },

      bulkInviteOpen: false,
      bulkFile:       null,

      toast:  useToast(),
      store:  useStore(),
    };
  },

  computed: {
    permission() {
      return this.store.state.getCurrentUserPermission;
    },
  },

  created() {
    this.fetchEmployees();
  },

  methods: {
    // -- Fetch ----------------------------------------------------------
    fetchEmployees() {
      this.loading = true;
      axios.get('/payroll/employees-invite', { params: this.tableData })
        .then(({ data }) => {
          this.employees = Array.isArray(data.data)
            ? data.data
            : Array.isArray(data.data?.data) ? data.data.data : [];
          const ids = new Set(this.employees.map(d => d.id));
          this.selectedModel = this.selectedModel.filter(id => ids.has(id));
          if (!this.selectedModel.length) this.allSelected = false;
        })
        .catch(err => {
          this.permissionStatus = err?.response?.status ?? null;
          if (err?.response?.status !== 403)
            this.toast.error(err.response?.data?.message ?? 'Could not load invites.');
        })
        .finally(() => { this.loading = false; });
    },

    selectAll() {
      this.selectedModel = this.allSelected ? this.employees.map(d => d.id) : [];
    },

    // -- Resend invite --------------------------------------------------
    openResendModal(emp) {
      this.resendTarget = emp;
      this.resendOpen   = true;
    },

    confirmResend() {
      if (!this.resendTarget || this.resendingId !== null) return;
      this.resendingId = this.resendTarget.id;
      const emp = this.resendTarget;
      axios.post(`/payroll/employees-invite/${emp.id}/resend`)
        .then(({ data }) => {
          this.toast.success(data?.message || `Invitation resent to ${emp.email}.`);
          this.resendOpen   = false;
          this.resendTarget = null;
          this.fetchEmployees();
        })
        .catch(err => {
          this.toast.error(this.flatError(err.response?.data) ?? 'Could not resend invitation.');
        })
        .finally(() => { this.resendingId = null; });
    },

    // -- Invite ---------------------------------------------------------
    submitInvite() {
      this.crudLoading = true;
      axios.post('/payroll/employees-invite', this.inviteForm)
        .then(({ data }) => {
          this.toast.success(data.message || 'Invitation sent successfully.');
          this.inviteOpen = false;
          this.fetchEmployees();
        })
        .catch(err => {
          const status = err.response?.status;
          if (status === 422) this.toast.warning(this.flatError(err.response.data.message));
          else this.toast.error(err.response?.data?.message ?? 'Could not send invitation.');
        })
        .finally(() => { this.crudLoading = false; });
    },

    // -- Bulk invite ----------------------------------------------------
    downloadTemplate() {
      // Build CSV with headers matching the expected format
      const headers  = ['first_name', 'last_name', 'official_email', 'personal_email'];
      const examples = [
        ['John',   'Doe',     'john.doe@company.com',       'john.personal@gmail.com'],
        ['Jane',   'Smith',   'jane.smith@company.com',     'jane.smith@yahoo.com'],
        ['Samuel', 'Johnson', 'samuel.johnson@company.com', ''],
      ];
      const csvContent = [headers.join(','), ...examples.map(r => r.join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = 'bulk_invite_template.csv';
      link.click();
      URL.revokeObjectURL(url);
    },

    onBulkFileDrop(e) { this.bulkFile = e.dataTransfer.files[0] ?? null; },

    submitBulkInvite() {
      if (!this.bulkFile) return;
      this.crudLoading = true;
      const form = new FormData();
      form.append('file', this.bulkFile, this.bulkFile.name);
      axios.post('/payroll/employees-invite/bulk', form)
        .then(({ data }) => {
          this.toast.success(data.message || 'Bulk invites sent successfully.');
          this.bulkInviteOpen = false;
          this.bulkFile = null;
          this.fetchEmployees();
        })
        .catch(err => {
          const status = err.response?.status;
          if (status === 422) this.toast.warning(this.flatError(err.response.data.message));
          else this.toast.error(err.response?.data?.message ?? 'Bulk invite failed.');
        })
        .finally(() => { this.crudLoading = false; });
    },


    // -- Bulk delete ----------------------------------------------------
    async confirmBulkDelete() {
      this.crudLoading = true;
      try {
        const { data } = await axios.post('/payroll/employees-invite/delete-bulk', {
          employees: this.selectedModel,
        });
        this.toast.success(data.message || `${this.selectedModel.length} invite(s) deleted.`);
        this.bulkDeleteOpen = false;
        this.selectedModel  = [];
        this.allSelected    = false;
        this.fetchEmployees();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) this.toast.error(this.flatError(err.response?.data?.message));
        else this.toast.error(err.response?.data?.message ?? 'Bulk delete failed.');
      } finally {
        this.crudLoading = false;
      }
    },

    // -- Dropdown -------------------------------------------------------
    closeDropdown() { this.dropdownOpen = false; },

    handleNew(type) {
      this.dropdownOpen = false;
      if (type === 'invite') {
        this.inviteForm = { first_name: '', last_name: '', email: '', personal_email: '' };
        this.inviteOpen = true;
      } else if (type === 'bulk_invite') {
        this.bulkFile = null;
        this.bulkInviteOpen = true;
      } else if (type === 'upload') {
        this.uploadFile       = null;
        this.uploadDepartment = '';
        this.uploadOpen       = true;
      }
    },

    // -- Debounce -------------------------------------------------------
    debouncedFetch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.fetchEmployees(), 300);
    },

    // -- Helpers --------------------------------------------------------
    getInitials(first, last) {
      const a = first?.trim()?.charAt(0) || '';
      const b = last?.trim()?.charAt(0)  || '';
      return (a + b).toUpperCase() || '?';
    },

    inviteStatusClass(status) {
      const map = {
        invited:   'status-badge--blue',
        completed: 'status-badge--green',
        accepted:  'status-badge--green',
        cancelled: 'status-badge--red',
        expired:   'status-badge--amber',
      };
      return map[(status || '').toLowerCase()] ?? 'status-badge--gray';
    },

    flatError(data) {
      if (!data || typeof data !== 'object') return null;
      const msg = data.message;
      if (typeof msg === 'string') return msg;
      if (Array.isArray(msg)) return msg[0];
      if (typeof msg === 'object') return Object.values(msg).flat()[0] ?? null;
      const errs = data.data;
      if (errs && typeof errs === 'object') return Object.values(errs).flat()[0] ?? null;
      return null;
    },
  },
};
</script>


<style scoped>
/* Page */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
.me-1 { margin-right:4px; }
.mt-3 { margin-top:12px; }

/* Search */
.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* Buttons */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.45; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-ghost:hover { background:#e4eaf2; }
.btn-danger-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; position:relative; font-family:inherit; }
.btn-danger-outline:hover:not(:disabled) { background:#fff5f5; }
.btn-danger-outline:disabled { opacity:.45; cursor:not-allowed; }
.chevron { font-size:11px; }
.bulk-count { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; font-size:10px; font-weight:700; border-radius:50%; width:17px; height:17px; margin-left:2px; }

/* Dropdown */
.dropdown-wrap   { position:relative; }
.action-dropdown { position:absolute; right:0; top:calc(100% + 6px); z-index:200; background:#fff; border:1px solid #e4eaf2; border-radius:12px; box-shadow:0 12px 32px rgba(0,0,0,.12); min-width:240px; padding:6px; }
.action-dropdown-item { display:flex; align-items:center; gap:12px; width:100%; padding:10px 12px; border:none; background:transparent; border-radius:8px; cursor:pointer; text-align:left; transition:background .12s; font-family:inherit; }
.action-dropdown-item:hover { background:#f4f7fb; }
.action-dropdown-icon { width:32px; height:32px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
.action-dropdown-label { font-size:13px; font-weight:600; color:#1a2b45; margin-bottom:2px; }
.action-dropdown-hint  { font-size:11.5px; color:#7a90b0; }

/* Data card */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* States */
.state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
.state-icon--red  { background:#fff0f0; color:#e03535; }
.state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub        { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell  { color:#4a5e78; font-size:13px; }

/* Table toolbar */
.table-toolbar { display:flex; align-items:center; padding:14px 20px; border-bottom:1px solid #e4eaf2; }
.table-count   { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.selected-hint { color:#004AAD; }

/* Table */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody tr.row--selected { background:#edf4ff; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.col-check   { width:40px; }
.col-actions { width:80px; text-align:center; }
.row-clickable { cursor:pointer; }

/* Employee cell */
.emp-cell   { display:flex; align-items:center; gap:10px; }
.emp-avatar { width:34px; height:34px; border-radius:50%; background:#004AAD; color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.emp-name   { font-size:13px; font-weight:600; color:#1a2b45; line-height:1.3; }

/* Status badges */
.status-badge       { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:3px 10px; text-transform:capitalize; }
.status-badge--blue  { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
.status-badge--green { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
.status-badge--red   { background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
.status-badge--amber { background:#fefce8; color:#ca8a04; border:1px solid #fde68a; }
.status-badge--gray  { background:#f4f7fb; color:#64748b; border:1px solid #e4eaf2; }

/* Action buttons */
.action-btns { display:flex; align-items:center; justify-content:center; gap:6px; }
.action-icon-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
.action-icon-btn:hover:not(:disabled) { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
.action-icon-btn:disabled { opacity:.5; cursor:not-allowed; }
.action-icon-btn--blue { border-color:#bfdbfe; color:#2563eb; background:#eff6ff; }
.action-icon-btn--blue:hover:not(:disabled) { background:#dbeafe; border-color:#93c5fd; }
.action-icon-btn--loading { opacity:.7; }

/* Modals */
.modal-overlay      { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:480px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-hd   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:flex-start; gap:12px; }
.modal-hd-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.modal-ttl  { font-size:15px; font-weight:700; color:#1a2b45; display:block; margin-bottom:2px; }
.modal-sub  { font-size:12px; color:#7a90b0; margin:0; }
.modal-close-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; margin-left:auto; flex-shrink:0; transition:background .15s; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-bd   { padding:20px 24px; display:flex; flex-direction:column; gap:14px; }
.modal-ft   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }

/* Fields */
.field-group  { display:flex; flex-direction:column; gap:5px; }
.field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
.req          { color:#e03535; }
.field-input  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-select { appearance:auto; cursor:pointer; }

/* Upload zone */
.upload-zone { border:2px dashed #dde5f0; border-radius:10px; padding:32px 20px; text-align:center; cursor:pointer; transition:all .15s; }
.upload-zone:hover, .upload-zone--active { border-color:#004AAD; background:rgba(0,74,173,.03); }
.upload-icon  { font-size:28px; color:#7a90b0; margin-bottom:10px; }
.upload-label { font-size:13.5px; font-weight:600; color:#1a2b45; margin:0 0 4px; }
.upload-hint  { font-size:12px; color:#7a90b0; margin:0; }

.modal-box--sm   { max-width:380px; }
.modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
.modal-icon      { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:24px; }
.modal-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
.modal-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
.modal-body-clean { padding:16px 28px 8px; text-align:center; }
.modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
.modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
.modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; transition:background .15s; font-family:inherit; }
.modal-btn--cancel  { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
.modal-btn--cancel:hover { background:#e4eaf2; }
.modal-btn--primary { background:#004AAD; color:#fff; }
.modal-btn--primary:hover:not(:disabled) { background:#003b8e; }
.modal-btn--primary:disabled { opacity:.6; cursor:not-allowed; }
.modal-btn--danger { background:#e03535; color:#fff; }
.modal-btn--danger:hover:not(:disabled) { background:#c02828; }
.modal-btn--danger:disabled { opacity:.6; cursor:not-allowed; }

/* Template hint */
.template-hint { display:flex; align-items:center; gap:8px; font-size:12.5px; color:#7a90b0; padding:10px 14px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; }
.template-hint-icon { color:#7a90b0; font-size:13px; flex-shrink:0; }
.btn-download-template { display:inline-flex; align-items:center; gap:5px; background:none; border:none; color:#004AAD; font-size:12.5px; font-weight:600; cursor:pointer; padding:0; font-family:inherit; text-decoration:underline; text-underline-offset:2px; margin-left:auto; white-space:nowrap; }
.btn-download-template:hover { color:#003b8e; }

/* Column reference */
.template-cols       { padding:12px 14px; background:#f0f4fb; border:1px solid #dde5f0; border-radius:8px; }
.template-cols-title { font-size:12px; font-weight:600; color:#1a2b45; margin:0 0 8px; }
.template-col-list   { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:6px; }
.col-tag             { font-size:11.5px; font-weight:600; padding:3px 10px; border-radius:20px; font-family:monospace; }
.col-tag--required   { background:rgba(0,74,173,.1); color:#004AAD; border:1px solid rgba(0,74,173,.2); }
.col-tag--optional   { background:#f4f7fb; color:#64748b; border:1px solid #dde5f0; }
.col-req             { color:#e03535; }
.template-cols-note  { font-size:11px; color:#7a90b0; margin:0; }

@media (max-width:768px) {
  .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  .search-input { width:100%; }
  .action-dropdown { right:auto; left:0; }
}
</style>